import { useEffect, useMemo, useState } from "react";
import { useMeasure } from "react-use";

export const useVerticalCarousel = ({ slideStep }: { slideStep: number }) => {
  const [offset, setOffset] = useState({ top: 0 });
  const [containerRef, { height: containerHeight }] =
    useMeasure<HTMLDivElement>();
  const [wrapperRef, { height: wrapperHeight }] = useMeasure<HTMLDivElement>();
  const [contentRef, { height: contentHeight }] = useMeasure<HTMLDivElement>();

  const hiddenHeight = useMemo(() => {
    return contentHeight - wrapperHeight;
  }, [contentHeight, wrapperHeight]);

  const isCarouselActive = useMemo(() => {
    return hiddenHeight > slideStep;
  }, [hiddenHeight, slideStep]);

  const canGoPrev = useMemo(() => {
    return offset.top < 0;
  }, [offset.top]);

  const canGoNext = useMemo(() => {
    return hiddenHeight > 0 && offset.top > -hiddenHeight;
  }, [offset.top, hiddenHeight]);

  const goPrev = () => {
    if (!canGoPrev) {
      return;
    }

    setOffset({ top: Math.min(offset.top + slideStep, 0) });
  };

  const goNext = () => {
    if (!canGoNext) {
      return;
    }

    setOffset({ top: Math.max(offset.top - slideStep, -hiddenHeight) });
  };

  useEffect(() => {
    if (!isCarouselActive) setOffset({ top: 0 });
  }, [isCarouselActive]);

  return {
    containerRef,
    wrapperRef,
    contentRef,
    goNext,
    goPrev,
    canGoNext,
    canGoPrev,
    offset,
    isCarouselActive,
  };
};
